// import { useActiveProfile } from '@/app/_lib/_hooks/useActiveProfile';
import { Message } from '../../../types/chat';
import { Avatar } from 'antd';
import { useEffect } from 'react';
import MessageText from './atoms/MessageText';
import MessageImages from './atoms/MessageImages';
import cn from 'classnames';
import MessageFiles from './atoms/MessageFiles';
import MessageStatus from './atoms/MessageStatus';
import { socket } from '../../../lib/socket';
import { useChat } from '../../../store/slices/chat';
import TitleText from '../../../components/atoms/TitleText';
import { chatService } from '../../../store/services/chatService';
import { RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';

import { useSearchParams } from 'react-router-dom';

type Props = Message;

const MessageBox = ({
  sender,
  text,
  files,
  images,
  timestamp,
  id,
  read,
  roomId,
}: Props) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { selectedRoomId } = useChat();
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.adminData?.id;
  const myMessage = String(sender?.id) === String(userId);
  const activeRoomId =
    roomId || selectedRoomId || searchParams.get('chatRoomId') || '';

  useEffect(() => {
    if (!read && !myMessage && activeRoomId) {
      socket.emit('chat-events', {
        type: 'seen',
        roomId: activeRoomId,
        messageId: id,
      });
      dispatch(chatService.util.invalidateTags(['CHAT_USERS']));
    }
  }, [id, myMessage, read, activeRoomId, dispatch]);

  return (
    <div
      className={cn('flex my-2', {
        'justify-end': myMessage,
        'justify-start': !myMessage,
      })}
      data-testid="chat-message-box-wrapper"
    >
      <div
        className={cn('flex gap-2.5 max-w-[85%] sm:max-w-[75%]', {
          'flex-row-reverse': myMessage,
        })}
        data-testid="chat-message-box"
      >
        <div className="min-w-fit flex-shrink-0" data-testid="chat-message-avatar">
          <Avatar
            src={sender.image}
            size={40}
            className="border-light-success border-2 overflow-hidden dark:border-dark-button-bg-primary"
          />
        </div>
        <div
          className={cn('flex flex-col gap-1 min-w-0', {
            'items-end': myMessage,
            'items-start': !myMessage,
          })}
          data-testid="chat-message-content"
        >
          <div className="flex items-center gap-x-1">
            <TitleText size="base" text={sender.name} />

            {!!sender.type && (
              <span className="capitalize text-xs text-text-secondary dark:text-dark-text-secondary">
                ({sender.type?.toLowerCase()})
              </span>
            )}
          </div>

          <div
            className={cn(
              'content flex flex-col gap-2 rounded-2xl p-2.5 sm:p-3 w-fit max-w-full break-words shadow-sm border border-light-input-border/40 dark:border-dark-input-border/40 bg-soft-background dark:bg-dark-surface-primary text-start',
              {
                'rounded-tr-none': myMessage,
                'rounded-tl-none': !myMessage,
              }
            )}
            data-testid="chat-message-bubble"
          >
            {!!text && <MessageText text={text} />}
            {!!files?.length && <MessageFiles files={files} />}
            {!!images?.length && (
              <MessageImages myMessage={myMessage} images={images} />
            )}
          </div>
          <MessageStatus
            read={read}
            timestamp={timestamp}
            myMessage={myMessage}
          />
        </div>
      </div>
    </div>
  );
};

export default MessageBox;

